import { useMemo } from "react";
import {
  applyFilters,
  applySearch,
  type FilterAccessor,
  type FilterPredicates,
} from "./dashboardData";
import { useDashboardQueryState } from "./useDashboardQueryState";

export interface UseDashboardDataOptions<T> {
  items: T[];
  searchKeys?: (keyof T)[];
  filterAccessor?: FilterAccessor<T>;
  filterPredicates?: FilterPredicates<T>;
  sortAccessor?: (item: T, id: string) => string | number;
  /** Distinguish multiple collections on one page (separate URL params). */
  namespace?: string;
}

export interface UseDashboardDataResult<T> {
  pageItems: T[];
  total: number;
  query: ReturnType<typeof useDashboardQueryState>;
}

export function useDashboardData<T>({
  items,
  searchKeys = [],
  filterAccessor,
  filterPredicates,
  sortAccessor,
  namespace,
}: UseDashboardDataOptions<T>): UseDashboardDataResult<T> {
  const query = useDashboardQueryState(namespace);
  const { search, filters, sort, page, pageSize } = query.state;

  const filtered = useMemo(() => {
    let rows = applySearch(items, search, searchKeys);
    rows = applyFilters(rows, filters, filterAccessor, filterPredicates);
    if (sort && sortAccessor) {
      rows = [...rows].sort((a, b) => {
        const av = sortAccessor(a, sort.id);
        const bv = sortAccessor(b, sort.id);
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [
    items,
    search,
    filters,
    sort,
    searchKeys,
    filterAccessor,
    filterPredicates,
    sortAccessor,
  ]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  return { pageItems, total: filtered.length, query };
}
